import { useEffect, useState } from 'react';
import { useAPI } from '../../../../clients/quipli-service/quipli-service-client';
import { UserDocument, UserDocumentType, UserDriverLic, User } from '../../../../clients/quipli-service/user/user';
import { Typography } from '../../../../components/typography/typography';
import Icon from '../../../../components/icons/icon';
import { UserDocumentForm } from '../../components/user-document-form';
import { InsuranceDocument } from '../../../../clients/customer-api/customer/customer-customer';
import { Button } from '../../../../components/form/button';
import { DocumentItem } from '../../components/document-item';
import { useLocalesAPI } from '../../../../clients/quipli-service/locales/locales-api-v1';
import { StateProvince } from '../../../../clients/quipli-service/locales/locales';
import { DateTime } from 'luxon';

export const Documents = ({ user, setUser }: { user: User; setUser: (u: any) => void }) => {
  const { getUserDocuments, getUserDriverLicense, getLabel, getInsuranceDocuments } = useAPI(),
    { searchStateProvinces } = useLocalesAPI(),
    [data, setData] = useState<UserDocument[]>([]),
    [isModalAddOpen, setModalAddOpen] = useState<boolean>(false),
    [otherCheckoutLbl, setOtherCheckoutLbl] = useState<string | undefined>(),
    [userDLDefault, setUserDLDefault] = useState<UserDriverLic | undefined>(),
    [insurance, setInsurance] = useState<InsuranceDocument>(),
    [stateProvinceList, setStateProvinceList] = useState<StateProvince[]>([]),
    [damageWaiver, setDamageWaiver] = useState<boolean>(user.userReqDamageWaiver ?? false);

  function fetchDocuments() {
    Promise.all([
      getUserDocuments(user.id),
      getUserDriverLicense(user.id),
      getLabel('LBL_CUSTOM_DOC_UPLOAD'),
      getInsuranceDocuments(user.id),
      searchStateProvinces(),
    ]).then(([documents, driverLic, label, insurance, stateProvinces]) => {
      setData(documents);
      setUserDLDefault(driverLic);
      setOtherCheckoutLbl(label ? label.caption : undefined);

      setInsurance(insurance[0]);
      setStateProvinceList(stateProvinces);
    });
  }

  useEffect(() => {
    fetchDocuments();
  }, [user.id]);

  return (
    <div className="tab-section mt-2">
      {data.length === 0 && !userDLDefault && !insurance ? (
        <div className="no_item_page">
          <Icon icon="FilePlus02" size="large" />
          <Typography type="text-sm" weight="medium" className="property text-grey-500">
            Looks like you haven't added any documents yet. <br></br> Click “Add Document” to get started
          </Typography>
          <button
            className="save-button"
            onClick={() => {
              setModalAddOpen(true);
            }}
          >
            Add Document
          </button>
        </div>
      ) : (
        <div className="w-fit min-w-[50%] flex flex-column gap-1">
          <div className="tab-title flex">
            <div>
              <Button
                className="flex gap-2 button button-yellow"
                onClick={() => {
                  setModalAddOpen(true);
                }}
              >
                <Typography type="text-sm" className="text-quipli-black-800">
                  Add document
                </Typography>
                <Icon icon="Add" size="medium" />
              </Button>
            </div>
          </div>
          {[
            ...data.filter(
              (doc) =>
                !(!!insurance && doc.documentType === UserDocumentType.Insurance) &&
                !(!!userDLDefault && doc.documentType === UserDocumentType.License),
            ), // standalone files
            userDLDefault && data.find((doc) => doc.documentType === UserDocumentType.License)
              ? data
                  .sort(
                    (a, b) =>
                      DateTime.fromISO(b.createdAt, { setZone: true }).toMillis() -
                      DateTime.fromISO(a.createdAt, { setZone: true }).toMillis(),
                  )
                  .find((doc) => doc.documentType === UserDocumentType.License) // Show the latest driver license
              : userDLDefault
              ? { documentType: UserDocumentType.License }
              : undefined,
            insurance && !insurance.file
              ? { documentType: UserDocumentType.Insurance }
              : insurance?.file
              ? insurance.file
              : undefined,
          ]
            .filter((item) => !!item)
            .map((item, index) => {
              return (
                <DocumentItem
                  key={`timeline-item-${index}`}
                  object={item as UserDocument}
                  driverLicense={userDLDefault}
                  reload={() => fetchDocuments()}
                  otherCheckoutLabel={otherCheckoutLbl}
                  userReqDamageWaiver={damageWaiver}
                  setUserReqDamageWaiver={(v) => {
                    setDamageWaiver(v);
                    setUser({ ...user, userReqDamageWaiver: v });
                  }}
                  user={user}
                  stateProvinceList={stateProvinceList}
                  insurance={insurance}
                ></DocumentItem>
              );
            })}
        </div>
      )}
      <UserDocumentForm
        isOpen={isModalAddOpen}
        setIsOpen={setModalAddOpen}
        isEdit={false}
        userDocument={
          {
            id: 0,
            userId: user.id,
            fileName: '',
            fileType: '',
          } as UserDocument
        }
        userDriverLicense={userDLDefault}
        customLabel={otherCheckoutLbl}
        user={user}
        onSubmit={fetchDocuments}
        insuranceDocument={insurance}
        stateProvinceList={stateProvinceList}
      ></UserDocumentForm>
    </div>
  );
};
